import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IAlreadySendToSignValidation, IDocumentFileResponse, IDocumentFilesResponse, IDocumentResponse, IResendDocumentsResponse } from '../models/document.model';
import { IDocumentsLinkResponse, IFormFieldsAnswersRequest, IFormFieldsRequest, IFormStructure } from '../models/form.interface';
import { IContractDetailPaged } from '../models/contract-detail.model';
import { GlobalConfigService } from './global-config.service';


@Injectable({
  providedIn: 'root'
})
export class DocumentsService {
  private get baseUrl(): string {
    return this.globalConfig.getApiUrl() + '/documents';
  }

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService
  ) { }

  setSendingToSign(isSending: boolean) {
    localStorage.setItem('isSendingToSign', btoa(isSending.toString()));
  }

  getSendingToSign(): boolean {
    return atob(localStorage.getItem('isSendingToSign') || '') === 'true';
  }

  

  generate(request: IFormFieldsAnswersRequest): Promise<Array<IDocumentsLinkResponse>> {
    return new Promise((resolve, reject) => {
      this.http.post<Array<IDocumentsLinkResponse>>(`${this.baseUrl}/answer`, request)
        .subscribe({
          next: resolve,
          error: (err) => {
            reject(err?.error);
          }
        });
    })
  }

  

  sendDocumentToSign(idList: string[], document: IDocumentFilesResponse, patientId: string): Promise<IAlreadySendToSignValidation> {
    return new Promise((resolve, reject) => {
      let idListParam = idList.join(',');
      this.http.post<IAlreadySendToSignValidation>(`${this.baseUrl}/send_to_sign?id_list=${idListParam}&patient_id=${patientId}`, document)
        .subscribe({
          next: resolve,
          error: (err) => {
            reject(err?.error);
          }
        });
    });
  }

  getAlreadySentDocuments(answerGroupId: string, documentsIds: string[]): Promise<IAlreadySendToSignValidation> {
    return new Promise((resolve, reject) => {
      this.http.get<IAlreadySendToSignValidation>(`${this.baseUrl}/send_to_sign/docs?answer_group_id=${answerGroupId}&documents_ids=${documentsIds.join(',')}`)
        .subscribe({
          next: resolve,
          error: (err) => {
            reject(err?.error);
          }
        });
    });
  }

  getDocuments(patientId: string, pageNumber: number, pageSize: number): Promise<IContractDetailPaged> {
    return new Promise((resolve, reject) => {
      this.http.get<IContractDetailPaged>(`${this.baseUrl}/patient_list`, {
        params: {
          pageNumber: pageNumber.toString(),
          pageSize: pageSize.toString(),
          patientId: patientId || ''
        }
      }).subscribe({
        next: (response) => {
          resolve({
            data: response.data,
            current_page: response.page_info?.current_page ?? response.current_page ?? 0,
            total_pages: response.page_info?.total_pages ?? response.total_pages ?? 1,
            total_elements: response.page_info?.total_elements ?? response.total_elements ?? response.data.length,
            elements_per_page: response.page_info?.elements_per_page ?? response.elements_per_page ?? pageSize
          });
        },
        error: (err) => {
          reject(err?.error);
        }
      });
    });
  }

  getDocumentsWithFilters(patientId: string, pageNumber: number, pageSize: number, filterType: string, initialDate: string, finalDate: string, moduleId: string): Promise<IContractDetailPaged> {
    return new Promise((resolve, reject) => {
      this.http.get<IContractDetailPaged>(`${this.baseUrl}/patient_list`, {
        params: {
          pageNumber: pageNumber.toString(),
          pageSize: pageSize.toString(),
          patientId: patientId || '',
          filterType,
          initial_date: initialDate === "" ? '2022-01-01' : initialDate,
          final_date: finalDate === "" ? new Date().toISOString().split('T')[0] : finalDate,
          module: moduleId || ''
        }
      }).subscribe({
        next: (response) => {
          resolve({
            data: response.data,
            current_page: response.page_info?.current_page ?? 0,
            total_pages: response.page_info?.total_pages ?? 1,
            total_elements: response.page_info?.total_elements ?? response.data.length,
            elements_per_page: response.page_info?.elements_per_page ?? pageSize
          });
        },
        error: (err) => {
          reject(err?.error);
        }
      });
    });
  }

  /**
   * Obtém as URLs de assinatura de documentos pendentes para reenvio
   * @param answerGroupId ID do grupo de respostas (identificador único do procedimento/contratação)
   */
  getPendingDocumentUrls(answerGroupId: string): Promise<IResendDocumentsResponse> {
    return new Promise((resolve, reject) => {
      this.http.get<IResendDocumentsResponse>(`${this.baseUrl}/pending/${answerGroupId}/signature-urls`)
        .subscribe({
          next: resolve,
          error: (err) => {
            reject(err?.error);
          }
        });
    });
  }

  /**
   * Deleta um grupo de documentos/respostas (answer group) associado a um paciente.
   * Retorna o payload retornado pelo backend (por exemplo: { success: boolean, message: string }).
   * NOTE: Faz uma suposição razoável da rota de API para exclusão do grupo de respostas.
   * Se a rota for diferente no backend, ajuste o caminho para o endpoint correto.
   */
  deleteAnswerGroup(answerGroupId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      // The backend expects a POST to perform the delete/manage action for an answer group
      // using an empty body. Use POST instead of DELETE.
      this.http.post<any>(`${this.baseUrl}/manage/${answerGroupId}`, {})
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => reject(err?.error)
        });
    });
  }
}
